import {
  createNewTask,
  deleteTask,
  deleteTaskAttachment,
  fetchTaskAttachmentById,
  fetchTaskById,
  fetchTasksList,
  updateTask,
} from '../api';
import { Env } from '@/shared/config/environment';

const TASKS_PER_PAGE_COUNT = 4;

const state = {
  activeFilters: {
    partner: null,
    period: {
      start: null,
      end: null,
    },
  },

  tasksList: null,
  scheduledDates: null,
  isLoadingTasks: false,
  tasksError: null,
  isUpdatingData: false,
  currentPage: 1,
  totalTasksCount: 0,
  taskStatistics: {
    newTasks: 0,
    finishedTasks: 0,
  },
};

const mutations = {
  SET_TASKS_LIST(state, tasksList) {
    state.tasksList = tasksList;
  },
  SET_PAGINATION(state, { currentPage, totalTasksCount }) {
    state.currentPage = currentPage;
    state.totalTasksCount = totalTasksCount;
  },
  SET_SCHEDULED_DATES(state, dates) {
    state.scheduledDates = dates;
  },
  SET_STATISTICS(state, { newTasks, finishedTasks }) {
    state.taskStatistics.newTasks = newTasks;
    state.taskStatistics.finishedTasks = finishedTasks;
  },
  SET_LOADING_TASKS(state, isLoading) {
    state.isLoadingTasks = isLoading;
  },
  SET_TASKS_ERROR(state, error) {
    state.tasksError = error;
  },
  SET_ACTIVE_FILTERS(state, filter) {
    state.activeFilters = filter;
  },
  SET_UPDATING_DATA(state, isUpdating) {
    state.isUpdatingData = isUpdating;
  },
};

const actions = {
  clearTasksList({ commit }) {
    commit('SET_TASKS_LIST', null);
    commit('SET_PAGINATION', { currentPage: 1, totalTasksCount: 0 });
    commit('SET_SCHEDULED_DATES', null);
    commit('SET_STATISTICS', { newTasks: 0, finishedTasks: 0 });
  },
  async fetchTasksList({ state, commit, dispatch }) {
    commit('SET_LOADING_TASKS', true);
    commit('SET_TASKS_ERROR', null);
    commit('SET_TASKS_LIST', null);

    try {
      const response = await fetchTasksList( {
        userId: state.activeFilters.partner,
        startDate: state.activeFilters.period.start,
        endDate: state.activeFilters.period.end,
        page: state.currentPage,
        pageSize: TASKS_PER_PAGE_COUNT,
      });

      const currentPage = response?.tasks?.current_page || 1;
      const totalTasksCount = response?.tasks?.total || 0;
      const newTasks = response?.statistics?.new || 0;
      const finishedTasks = response?.statistics?.finished || 0;

      commit('SET_TASKS_LIST', response?.tasks?.data || []);
      commit('SET_PAGINATION', { currentPage, totalTasksCount });
      commit('SET_SCHEDULED_DATES', response?.event_dates?.map((event) => { return { date: event }; }) || []);
      commit('SET_STATISTICS', { newTasks, finishedTasks });
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити дані про завдання';

      commit('SET_TASKS_ERROR', errorMessage);
      dispatch('clearTasksList');
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити завдання',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_TASKS', false);
    }
  },
  setupFilter: ({ state, commit, dispatch }, { filters, mode = 'replace', refetchData = true }) => {
    if (mode === 'replace') {
      commit('SET_PAGINATION', { currentPage: 1, totalTasksCount: 0 });
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_PAGINATION', { currentPage: 1, totalTasksCount: 0 });
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }

    if (refetchData) {
      dispatch('fetchTasksList');
    }
  },
  changePaginatedPage({ state, commit, dispatch }, page) {
    commit('SET_PAGINATION', { currentPage: page, totalTasksCount: state.totalTasksCount });
    dispatch('fetchTasksList');
  },
  async getTask({ dispatch }, taskId) {
    try {
      return await fetchTaskById(taskId);
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити дані про завдання';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити завдання',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    }
  },
  async updateTaskStatus({ commit, dispatch }, { taskId, status }) {
    try {
      commit('SET_UPDATING_DATA', true);

      const response = await updateTask({ id: taskId, status: status });
      dispatch('fetchTasksList');

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося змінити статус завдання';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося змінити статус завдання',
          message: errorMessage,
        }, { root: true });
      }
    } finally {
      commit('SET_UPDATING_DATA', false);
    }
  },
  async createNewTask({ commit, dispatch }, { name = null, description = null, finishDate = null, status = null }) {
    try {
      commit('SET_UPDATING_DATA', true);
      const response = await createNewTask({ name, description, finishDate, status });

      dispatch('fetchTasksList');

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося створити завдання';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося створити завдання',
          message: errorMessage,
        }, { root: true });
      }
    } finally {
      commit('SET_UPDATING_DATA', false);
    }
  },
  async updateTask({ commit, dispatch }, { id, name = null, description = null, finishDate = null, status = null }) {
    try {
      commit('SET_UPDATING_DATA', true);
      await updateTask({ id, name, description, finishDate, status });

      dispatch('fetchTasksList');
    } catch (error) {
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося оновити завдання',
          message: 'Не вдалося оновити завдання',
        }, { root: true });
      }
    } finally {
      commit('SET_UPDATING_DATA', false);
    }
  },
  async deleteTask({ commit, dispatch }, taskId) {
    try {
      commit('SET_UPDATING_DATA', true);

      await deleteTask(taskId);
      dispatch('fetchTasksList');
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося видалити завдання';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося видалити завдання',
          message: errorMessage,
        }, { root: true });
      }
    } finally {
      commit('SET_UPDATING_DATA', false);
    }
  },
  async deleteTaskAttachment({ commit, dispatch }, attachmentId) {
    try {
      commit('SET_UPDATING_DATA', true);

      await deleteTaskAttachment(attachmentId);
    } catch (error) {
      const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || 'Не вдалося видалити прикріплений до завдання файл';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося видалити прикріплений до завдання файл',
          message: errorMessage,
        }, { root: true });
      }
    } finally {
      commit('SET_UPDATING_DATA', false);
    }
  },
  async getTaskAttachment({ dispatch }, attachmentId) {
    try {
      return await fetchTaskAttachmentById(attachmentId);
    } catch (error) {
      const errorMessage = error.response?.data?.message
          || error.response?.data?.error
          || error.message
          || 'Не вдалося завантажити прикріплений до завдання файл';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити прикріплений до завдання файл',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    }
  },
};

const getters = {
  getActiveFilters: (state) => state.activeFilters,

  isLoadingTasks: (state) => state.isLoadingTasks,
  isUpdatingData: (state) => state.isUpdatingData,
  tasksError: (state) => state.tasksError,
  isTasksDataReady: (state) => state.isLoadingTasks === false && state.tasksList !== null,
  isEmpty: (state) => !state.tasksList || state.tasksList?.length === 0,
  getTasksList: (state) => state.tasksList,
  getScheduledDates: (state) => state.scheduledDates,
  getCurrentPage: (state) => state.currentPage,
  getPerPageCount: () => TASKS_PER_PAGE_COUNT,
  getTotalTasksCount: (state) => state.totalTasksCount,
  getNewTasksCount: (state) => state.taskStatistics.newTasks,
  getFinishedTasksCount: (state) => state.taskStatistics.finishedTasks,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
